import cookie from 'js-cookie'
import { v4 } from 'uuid'
import IVideoConfig, { IPlaylistSource } from '../@types/IVideoConfig'
import { IVideoOptions } from '../@types/IVideoOptions'
import { AUTOPLAY_DISABLED, PLAYLIST_POSITION } from '../constants/cookie'
import { BELOW_THE_FOLD, DEFAULT_VOLUME } from '../constants/player'
import { CLOUDINARY_SHIELD_URL, CLOUDINARY_URL } from '../constants/url'
import { AdUnit } from '../enums/AdUnit'
import Bidder from '../enums/Bidder'
import ContentType, { Mime, Stream } from '../enums/ContentType'
import PopoutPlacement from '../enums/PopoutPlacement'
import VideoStatus from '../enums/VideoStatus'
import { PLAYLIST_TARGET_PREFIX } from '../constants/css'
import { Device } from './device'
import { getPosition, isTruthyAttribute } from './dom'
import { detectMimeTypes } from './mimeTypes'
import { getQueryParamByName } from './url'

interface IDeviceProps {
  browser: string
  h265: boolean
  mobile: boolean
  os: string
  osversion: string
  tablet: boolean
  type: string
  vp9: boolean
}

type ITestBidKeys = keyof typeof Bidder
type ITestBids = { [key in ITestBidKeys]: boolean }

export default class PlayerOptions {
  public aboveTheFold: boolean
  public device: IDeviceProps
  public hasPrimaryVideo: boolean
  public mimes: Mime[]
  public npa: 1 | 0
  public playlist: IPlaylistSource[]
  public popoutPlacement: PopoutPlacement
  public popoutTopMargin: number
  public source: { src: string; type: ContentType }
  public targetDiv: HTMLDivElement
  public testBids: ITestBids
  public uuid: string

  constructor(
    public readonly config: IVideoConfig,
    public manualOptions: Partial<IVideoOptions>
  ) {
    this.targetDiv = this.getTargetDiv()
    this.uuid = v4()
    // ^ needs to be first because other property calculations are based on it

    this.aboveTheFold = this.getAboveTheFold()
    this.device = this.getDeviceDetails()
    this.hasPrimaryVideo = this.getHasPrimaryVideo()
    this.mimes = detectMimeTypes()
    this.playlist = this.createPlaylist()
    this.popoutPlacement = this.getPopoutPlacement()
    this.popoutTopMargin = this.getPopoutTopMargin()
    this.source = this.detectSource()
    this.testBids = this.getTestBids()
  }

  get adunitId(): string {
    return `${this.slug}__${this.uuid}`
  }

  get aspectFraction(): number {
    const [width, height] = this.aspectRatio.split(':')
    return parseFloat(width) / parseFloat(height)
  }

  get aspectRatio(): string {
    let ratio
    if (this.manualOptions.aspectRatio === 'default') {
      ratio = this.config.site.aspectRatio || '16:9'
    } else {
      ratio =
        this.manualOptions.aspectRatio || this.config.site.aspectRatio || '16:9'
    }
    return ratio === 'fluid' ? `${this.width}:${this.height}` : ratio
  }

  get autoplay(): boolean {
    if (
      this.config.video.disableAutoplayOnClose &&
      cookie.get(AUTOPLAY_DISABLED)
    ) {
      return false
    }

    // mobile
    if (this.manualOptions.isAutomobile && this.device.mobile) {
      return true
    }

    // desktop
    if (this.manualOptions.isAutoplay && !this.device.mobile) {
      return true
    }

    return false
  }

  get autoplayOnLoad(): boolean {
    return (
      (this.autoplay && this.aboveTheFold) || (this.autoplay && this.visible)
    )
  }

  set autoplayDesktop(val: boolean) {
    this.manualOptions.isAutoplay = val
  }

  set autoplayMobile(val: boolean) {
    this.manualOptions.isAutomobile = val
  }

  get customTargeting(): object | undefined {
    return this.manualOptions.customTargeting || {}
  }

  get contentHeight(): number {
    return Math.round(this.contentWidth / this.aspectFraction)
  }

  get contentWidth(): number {
    const wrapper = this.targetDiv
    if (!wrapper) {
      return 0
    }
    return wrapper.offsetWidth
  }

  get debug(): boolean {
    return (
      this.manualOptions.debug ||
      isTruthyAttribute(getQueryParamByName('debug', window.location.search))
    )
  }

  get disableJsonLD(): boolean {
    return !!this.manualOptions.disableJsonLD
  }

  get height(): number {
    return this.manualOptions.height || this.config.video.height || 0
  }

  get muted(): boolean {
    return !!(this.manualOptions.isMuted || this.autoplay)
  }

  get onAdStarted(): () => void {
    return this.manualOptions.onAdStarted || (() => null)
  }

  get onEnded(): () => void {
    return () => {
      if (this.manualOptions.onEnded) {
        this.manualOptions.onEnded()
      }
    }
  }

  get onError(): (...props) => void {
    return (error: Error | Event, extra: object = {}) => {
      let newError = error
      let newExtra = extra

      if (newError instanceof Event) {
        newError = new Error(newError.type)
        newExtra = { ...extra, eventProps: error }
      }

      if (this.manualOptions.onError) {
        this.manualOptions.onError(newError, newExtra)
      }
    }
  }

  get onViewable(): () => void {
    return this.manualOptions.onViewable || (() => null)
  }

  get permalink(): string {
    return (
      this.manualOptions.permalink ||
      this.config.video.permalink ||
      `${window.location.protocol}//${this.config.site.domain}`
    )
  }

  get prebid(): boolean {
    return (
      this.config.site.appnexus ||
      this.config.site.brealtime ||
      this.config.site.index ||
      this.config.site.oneVideo ||
      this.config.site.openx ||
      this.config.site.pubmatic ||
      this.config.site.rubicon
    )
  }

  get processing(): boolean {
    return this.config.video.status === VideoStatus.processing
  }

  get slug(): string {
    return this.config.video.slug || ''
  }

  // If the video was auto-inserted, we should use the
  // video-shield.mediavine.com endpoint instead.
  get shouldShield(): boolean {
    return this.manualOptions.autoInsert
  }

  get srcPoster(): string {
    let source

    if (this.manualOptions.srcPoster) {
      source = this.manualOptions.srcPoster
    } else if (this.hasPrimaryVideo) {
      source = this.config.video.thumbnail
    } else {
      const position = parseInt(cookie.get(PLAYLIST_POSITION), 10)
      source =
        isNaN(position) || position - 1 < 0
          ? this.config.video.thumbnail
          : this.playlist[position - 1].thumbnail
    }

    if (this.shouldShield) {
      source = source.replace(CLOUDINARY_URL, CLOUDINARY_SHIELD_URL)
    }

    return this.getAspectRatioThumbnail(source || '')
  }

  set srcPoster(src: string) {
    this.manualOptions.srcPoster = src
  }

  get sticky(): boolean {
    const sticky = !!this.manualOptions.sticky
    return !!(
      (sticky && this.manualOptions.isAutomobile && this.device.mobile) ||
      (sticky && !this.device.mobile)
    )
  }

  get targetID(): string {
    return this.manualOptions.targetID || this.config.targetID || this.slug
  }

  get test(): boolean {
    return !!getQueryParamByName('test', window.location.search)
  }

  get title(): string {
    return this.manualOptions.title || this.config.video.title
  }

  get unitType(): string {
    if (this.sticky && !this.device.mobile) {
      return AdUnit.sticky
    } else {
      return this.manualOptions.unitType || AdUnit.video
    }
  }

  get aboveViewport(): boolean {
    if (!this.targetDiv) {
      return false
    }

    const bounding = this.targetDiv.getBoundingClientRect()
    const vh = window.innerHeight || document.documentElement.clientHeight

    return bounding.bottom < vh
  }

  get belowViewport(): boolean {
    if (!this.targetDiv) {
      return false
    }

    const bounding = this.targetDiv.getBoundingClientRect()
    const vh = window.innerHeight || document.documentElement.clientHeight

    return bounding.bottom > vh
  }

  get visible(): boolean {
    if (!this.targetDiv) {
      return false
    }

    const bounding = this.targetDiv.getBoundingClientRect()
    const visibilityThreshold = this.contentHeight / 2
    const vh = window.innerHeight || document.documentElement.clientHeight

    return (
      bounding.top > 0 - visibilityThreshold &&
      bounding.bottom < vh + visibilityThreshold
    )
  }

  get volume(): number {
    return typeof this.manualOptions.volume === 'number'
      ? this.manualOptions.volume
      : DEFAULT_VOLUME
  }

  get width(): number {
    return this.manualOptions.width || this.config.video.width || 0
  }

  private getAboveTheFold(): boolean {
    if (!this.targetDiv) {
      return false
    }

    const contentPosition = getPosition(this.targetDiv)
    return contentPosition.y <= Math.max(BELOW_THE_FOLD, window.innerHeight)
  }

  private detectSource(): { src: string; type: Stream } {
    let source
    if (this.device.vp9) {
      source = {
        src: this.manualOptions.srcDash || this.config.video.mpd || '',
        type: Stream.dash
      }
    } else if (this.device.h265) {
      source = {
        src: this.manualOptions.srcHlsH265 || this.config.video.hlsH265 || '',
        type: Stream.hls
      }
    } else {
      source = {
        src: this.manualOptions.srcHls || this.config.video.m3u8 || '',
        type: Stream.hls
      }
    }

    if (this.shouldShield) {
      source.src = source.src.replace(CLOUDINARY_URL, CLOUDINARY_SHIELD_URL)
    }

    return source
  }

  private createPlaylist(): IPlaylistSource[] {
    if (!Array.isArray(this.config.playlist)) {
      return []
    }

    return this.config.playlist.map((item) => {
      let src = item.stream.h264
      let srcType = Stream.hls

      if (this.device.vp9) {
        src = item.stream.vp9
        srcType = Stream.dash
      } else if (this.device.h265) {
        src = item.stream.h265
      }

      let thumbnail = item.thumbnail
      if (this.shouldShield) {
        src = src.replace(CLOUDINARY_URL, CLOUDINARY_SHIELD_URL)
        thumbnail = thumbnail.replace(CLOUDINARY_URL, CLOUDINARY_SHIELD_URL)
      }

      return {
        duration: item.duration,
        keywords: item.keywords || '',
        permalink: item.permalink,
        slug: item.slug,
        src,
        srcType,
        thumbnail: this.getAspectRatioThumbnail(thumbnail),
        title: item.title,
        tracks: item.tracks,
        videoNumber: item.videoNumber,
        viewed: false
      }
    })
  }

  private getPopoutPlacement(): PopoutPlacement {
    if (this.device.mobile) {
      return this.config.site.mobilePopoutPlacement
    }

    const stickyContentSide = !!this.config.site.stickyContentSide
    const contentSelector = this.config.site.contentSelector || null
    const sidebarSelector = this.config.site.sidebarSelector || null
    const contentEl = document.querySelector(contentSelector) as HTMLElement
    const sidebarEl = document.querySelector(sidebarSelector) as HTMLElement
    let contentLeft: number | boolean
    let sidebarLeft: number | boolean

    try {
      contentLeft = contentEl && contentEl.getBoundingClientRect().left
      sidebarLeft = sidebarEl && sidebarEl.getBoundingClientRect().left
    } catch (e) {
      sidebarLeft = false
      contentLeft = false
    }

    if (typeof sidebarLeft !== 'number' || typeof contentLeft !== 'number') {
      return PopoutPlacement.bottomRight
    }

    if (stickyContentSide) {
      return sidebarLeft < contentLeft
        ? PopoutPlacement.bottomRight
        : PopoutPlacement.bottomLeft
    }
    return sidebarLeft < contentLeft
      ? PopoutPlacement.bottomLeft
      : PopoutPlacement.bottomRight
  }

  private getPopoutTopMargin(): number {
    const defaultMargin = 15
    return this.device.mobile
      ? defaultMargin + this.config.site.mobilePopoutTopMargin
      : 0
  }

  private getTargetDiv(): HTMLDivElement {
    if (this.config.targetDiv) {
      return this.config.targetDiv
    }
    const nodes = document.querySelectorAll(`div[id='${this.targetID}']`)
    const divs = Array.from(nodes) as HTMLDivElement[]
    return divs.find((div) => div.childElementCount === 0)
  }

  private getTestBids(): ITestBids {
    const qp =
      getQueryParamByName('video_test_bids', window.location.search) || ''
    return Object.keys(Bidder).reduce((acc, key) => {
      return { ...acc, [key]: qp.indexOf(key) !== -1 }
    }, {}) as ITestBids
  }

  private getDeviceDetails(): IDeviceProps {
    return {
      browser: Device.browser,
      h265: Device.supportsH265,
      mobile: !!(Device.isTablet || Device.isMobile),
      os: Device.os,
      osversion: Device.osversion,
      tablet: Device.isTablet,
      type: Device.deviceType,
      vp9: Device.supportsVP9
    }
  }

  private getHasPrimaryVideo(): boolean {
    return this.targetID.indexOf(PLAYLIST_TARGET_PREFIX) === -1
  }

  private getAspectRatioThumbnail(thumbnail: string): string {
    let dimensions = { width: 640, height: 360 }

    if (this.aspectRatio === '16:9') {
      dimensions = { width: 640, height: 360 }
    } else if (this.aspectRatio === '9:16') {
      dimensions = { width: 360, height: 640 }
    } else if (this.aspectRatio === '4:5') {
      dimensions = { width: 512, height: 640 }
    } else if (this.aspectRatio === '3:4') {
      dimensions = { width: 480, height: 640 }
    } else if (this.aspectRatio === '1:1') {
      dimensions = { width: 640, height: 640 }
    }

    const newThumbnail = thumbnail
      .replace(/(h_)[0-9]+/, `h_${dimensions.height}`)
      .replace(/(w_)[0-9]+/, `w_${dimensions.width}`)
      .replace(/(ar_)[0-9]+(:)[0-9]+/, `ar_${this.aspectRatio}`)

    return newThumbnail
  }
}
